import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { shape } from "@app/design-tokens";
import { Fragment } from "react";
import { Link, useLocation } from "react-router";

import { useAuth } from "@/auth/authContext";
import { pageHref, visibleServicePages } from "@/services/registry";

import { serviceIcon } from "./serviceIcons";

/**
 * 「服務」這一節的第二層內容：**所有服務與它們的所有子頁，一份完整的目錄。**
 *
 * 這裡與頁面上方的 tab 都能切換子頁，那個重複是刻意的，因為兩者做的不是同
 * 一件事：
 *
 *   tab   在**目前這個服務內**換面向（儀表／管理／設定）
 *   目錄  跨服務直接跳到**別的服務的某一個面向**，一次點擊
 *
 * 沒有目錄的話，從 A 服務的「管理」到 B 服務的「設定」要先進 B 再點 tab，
 * 兩步。服務會長到七個以上，跨服務跳轉是這個平台的日常操作，所以那一步值得
 * 省下來。
 *
 * 代價是這份清單會隨服務數量成長（七個服務約 28 列，需要滾動）。真的太長時
 * 下一步是讓每個服務群組可收合並記住狀態，而不是砍掉子頁。
 *
 * 服務清單來自 `/me/`，子頁來自各服務自己的 `pages.ts` 並依權限過濾 ——
 * 前端不維護任何服務、角色或權限的清單。
 */

const ITEM_SX = {
  borderRadius: `${shape.medium}px`,
  minHeight: 40,
  px: 1.5,
  "& .MuiListItemIcon-root": { minWidth: 34, color: "inherit" },
  "&.Mui-selected": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    "&:hover": { bgcolor: "primary.main" },
  },
} as const;

/**
 * 子頁比服務矮一階、字小一號，靠左邊那條線表達從屬關係。不放 icon——
 * 服務那一列已經有一個能代表整個服務的圖示，子頁再各配一個只是重複的
 * 視覺噪音，而且子頁一多（品項／廠區／料號…）要找到有意義又好認的圖示
 * 集合並不容易。
 */
const SUB_ITEM_SX = {
  borderRadius: `${shape.small}px`,
  minHeight: 34,
  px: 1.25,
  "&.Mui-selected": {
    bgcolor: "action.selected",
    color: "text.primary",
  },
} as const;

interface ServiceNavProps {
  /** 點了任一項之後要做的事（手機版用來關閉浮層） */
  onNavigate?: () => void;
}

export function ServiceNav({ onNavigate }: ServiceNavProps) {
  const { me } = useAuth();
  const location = useLocation();
  const services = me?.services ?? [];

  if (services.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
        尚未被授予任何服務權限。
      </Typography>
    );
  }

  return (
    <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {services.map((service) => {
        const Icon = serviceIcon(service.code);
        const root = `/${service.code}`;
        const isCurrentService =
          location.pathname === root || location.pathname.startsWith(`${root}/`);

        // **所有**服務的子頁都列出來，不只目前這一個 —— 目錄的意義就在這裡
        const pages = visibleServicePages(service.code, me);
        const hasSubPages = pages.length > 1;

        return (
          <Fragment key={service.code}>
            <ListItemButton
              component={Link}
              to={root}
              // 有子頁時選中狀態由子項表達，服務那一列只是群組標題兼入口；
              // 兩處同時反白會讓人以為選了兩個東西
              selected={isCurrentService && !hasSubPages}
              aria-current={isCurrentService && !hasSubPages ? "page" : undefined}
              onClick={onNavigate}
              sx={ITEM_SX}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={service.name}
                slotProps={{
                  primary: {
                    variant: "subtitle2",
                    // 目前所在的服務加粗，在 28 列的清單裡才找得到自己
                    sx: { fontWeight: isCurrentService ? 700 : 600 },
                  },
                }}
              />
            </ListItemButton>

            {hasSubPages && (
              <Box
                sx={{
                  ml: 2.5,
                  pl: 1.25,
                  borderLeft: 1,
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                  py: 0.25,
                  mb: 0.5,
                }}
              >
                {pages.map((page) => {
                  const href = pageHref(service.code, page.path);
                  const isCurrentPage =
                    page.path === ""
                      ? location.pathname === href
                      : location.pathname === href || location.pathname.startsWith(`${href}/`);

                  return (
                    <ListItemButton
                      key={page.path || "index"}
                      component={Link}
                      to={href}
                      selected={isCurrentPage}
                      aria-current={isCurrentPage ? "page" : undefined}
                      onClick={onNavigate}
                      sx={SUB_ITEM_SX}
                    >
                      <ListItemText
                        primary={page.label}
                        slotProps={{
                          primary: {
                            variant: "body2",
                            sx: { fontWeight: isCurrentPage ? 600 : 400 },
                          },
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </Box>
            )}
          </Fragment>
        );
      })}
    </List>
  );
}
